import { Button, NotificationsMenu, PageHeader as PageHeaderUI } from '@blend-ed/blendx-ui';
import { getConfig } from '@edx/frontend-platform';
import { getCookies } from '@edx/frontend-platform/i18n/lib';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useHeaderTitle } from '../../context/HeaderTitle';
import { useCartData } from '../../hooks/useCart';
import { useNotifications } from '../../hooks/useNotifications';
import { useNotificationsRead } from '../../hooks/useNotificationsRead';
import { useFetchUserNotifications as useStaffNotifications } from '../../hooks/useStaffNotifications';
import { useStreakData } from '../../hooks/useStreakData';
import { getFeatureToggle } from '../../utils/getFeatureToggle';
import CartDrawer from './CartDrawer';
import DarkModeToggle from './DarkModeToggle';
import DemoVideoModal from './DemoVideoModal';
import LanguageSelector from './LanguageSelector/LanguageSelector';

const PageHeader = ({ staffDashboard, backLink, title: titleProp }) => {
  const streakData = useStreakData();
  const { parsedCartData, cartRefetch } = useCartData();
  const { title } = titleProp !== null ? { title: titleProp } : useHeaderTitle();
  const { data: notificationData, refetch: notificationRefetch } = useNotifications();
  const { data: staffNotificationData, refetch: staffNotificationRefetch } = useStaffNotifications();
  const { markAsRead } = useNotificationsRead();
  const { STAFF_DASHBOARD_URL, DASHBOARD_URL, DEMO_INSTANCE, LMS_BASE_URL } = getConfig();
  const { features_enable_dark_toggle } = getFeatureToggle();
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Cookie logic for demo video modal
  const DEMO_VIDEO_COOKIE = 'demoVideoModalShown';
  const cookies = getCookies();
  const domain = `.${LMS_BASE_URL.split('://')[1].split(':')[0]}`;
  const hasSeenDemoVideo = cookies.get(DEMO_VIDEO_COOKIE);
  const [showVideoModal, setShowVideoModal] = useState(() => {
    // Show by default if not seen before and DEMO_INSTANCE is true
    return DEMO_INSTANCE && !hasSeenDemoVideo;
  });

  const handleHideVideoModal = () => {
    // Set cookie to never expire (far future date)
    cookies.set(DEMO_VIDEO_COOKIE, 'true', { path: '/', domain, expires: new Date('2099-12-31') });
    setShowVideoModal(false);
  };

  return (
    <>
      <PageHeaderUI
        staff={staffDashboard}
        streakData={streakData}
        cartCount={parsedCartData?.length}
        setIsCartOpen={setIsCartOpen}
        notificationMenu={
          <NotificationsMenu
            markAsRead={markAsRead}
            notifications={staffDashboard ? staffNotificationData?.userPushNotifications : notificationData?.userPushNotifications}
            refetchCallback={staffDashboard ? staffNotificationRefetch : notificationRefetch}
            toLink={staffDashboard ? STAFF_DASHBOARD_URL + '/notifications' : DASHBOARD_URL + '/notifications'}
          />
        }
        languageSelector={
          <LanguageSelector />
        }
        notificationCount={notificationData?.userPushNotifications?.filter(
          notification => !notification.readStatus
        )?.length}
        notificationLink={staffDashboard ? STAFF_DASHBOARD_URL + 'notifications' : DASHBOARD_URL + 'notifications'}
        goBackLink={backLink}
        darkModeToggle={features_enable_dark_toggle && <DarkModeToggle />}
        extraActions={DEMO_INSTANCE && (
          <Button variant="outline-gray" size="xs" onClick={() => setShowVideoModal(true)}>
            Watch Demo Video
          </Button>
        )}
      >
        {title}
      </PageHeaderUI>
      <CartDrawer
        cartData={parsedCartData}
        cartRefetch={cartRefetch}
        show={isCartOpen}
        onClose={() => setIsCartOpen(false)}
      />
      {DEMO_INSTANCE && <DemoVideoModal show={showVideoModal} onHide={handleHideVideoModal} />}
    </>
  );
};

PageHeader.propTypes = {
  title: PropTypes.string,
  staffDashboard: PropTypes.bool,
  backLink: PropTypes.string,
};

PageHeader.defaultProps = {
  title: null,
  staffDashboard: false,
  backLink: null,
};

export default PageHeader;